import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah, todayISO, toDateInput } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Boxes, Plus, X } from "lucide-react";

const kgFmt = (v) => `${Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} Kg`;

const BahanRows = ({ rows, setRows }) => (
  <div className="space-y-2" data-testid="briket-bahan-rows">
    {rows.map((r, i) => (
      <div key={i} className="flex gap-2">
        <Input value={r.nama} placeholder="mis. Tapioka / Residu" onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, nama: e.target.value } : x))} data-testid={`briket-bahan-nama-${i}`}/>
        <CurrencyInput value={r.biaya} onValueChange={(v) => setRows(rows.map((x, j) => j === i ? { ...x, biaya: v } : x))} data-testid={`briket-bahan-biaya-${i}`}/>
        <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="p-2 rounded-sm hover:bg-rose-50" data-testid={`briket-bahan-del-${i}`}><X size={14} className="text-rose-600"/></button>
      </div>
    ))}
    <Button type="button" variant="outline" size="sm" onClick={() => setRows([...rows, { nama: "", biaya: 0 }])} className="rounded-sm" data-testid="briket-bahan-add"><Plus size={13} className="mr-1"/>Tambah Bahan</Button>
  </div>
);

const StokRows = ({ stok, pakai, setPakai }) => (
  <div className="space-y-2" data-testid="briket-stok-rows">
    {stok.length === 0 && <div className="text-xs text-slate-400">Belum ada stok bahan. Catat di menu Bahan Masuk.</div>}
    {stok.map((b) => {
      const kg = Number(pakai[b.nama] || 0); const lebih = kg > b.tersedia + 1e-6;
      return (
        <div key={b.nama} className="grid grid-cols-[1fr_110px] sm:grid-cols-[1fr_140px] gap-2 items-center">
          <div className="min-w-0">
            <div className="text-sm text-slate-800 truncate">{b.nama}</div>
            <div className={`text-[11px] ${lebih ? "text-rose-600" : "text-slate-500"}`} data-testid={`briket-stok-info-${b.nama}`}>Tersedia {kgFmt(b.tersedia)} · {rupiah(b.harga_rata)}/Kg{lebih ? " · melebihi stok" : ""}</div>
          </div>
          <Input type="number" min="0" step="0.01" placeholder="Kg" value={pakai[b.nama] ?? ""} className={lebih ? "border-rose-400" : ""}
            onChange={(e) => setPakai({ ...pakai, [b.nama]: e.target.value })} data-testid={`briket-stok-kg-${b.nama}`}/>
        </div>
      );
    })}
  </div>
);

export const CetakBriketDialog = ({ open, setOpen, item, stokBahan = [], produkList, onSaved }) => {
  const [form, setForm] = useState({});
  const [rows, setRows] = useState([]);
  const [pakai, setPakai] = useState({});
  useEffect(() => {
    if (!open) return;
    setForm(item
      ? { tanggal_mulai: toDateInput(item.tanggal_mulai), produk_id: item.produk_id, biaya_operasional: item.biaya_operasional, durasi_hari: item.durasi_hari, keterangan: item.keterangan || "" }
      : { tanggal_mulai: todayISO(), produk_id: produkList[0]?.id || "", biaya_operasional: 0, durasi_hari: 3, keterangan: "" });
    setRows(item ? (item.bahan_luar || []) : [{ nama: "Tapioka", biaya: 0 }, { nama: "Residu", biaya: 0 }]);
    setPakai(Object.fromEntries((item?.bahan_stok || []).map((r) => [r.nama, r.kg])));
  }, [open, item]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stok tersedia = sisa + yang sudah dipakai batch ini (saat edit)
  const lama = Object.fromEntries((item?.bahan_stok || []).map((r) => [r.nama, r]));
  const stok = [...stokBahan.map((b) => b.nama), ...Object.keys(lama)].filter((n, i, a) => a.indexOf(n) === i).map((nama) => {
    const b = stokBahan.find((x) => x.nama === nama) || { sisa_kg: 0, nilai_sisa: 0 };
    const kgT = Number(b.sisa_kg || 0) + Number(lama[nama]?.kg || 0);
    const nilaiT = Number(b.nilai_sisa || 0) + Number(lama[nama]?.biaya || 0);
    return { nama, tersedia: kgT, harga_rata: kgT > 0 ? nilaiT / kgT : 0 };
  });
  const kg = stok.reduce((a, b) => a + Number(pakai[b.nama] || 0), 0);
  const lebih = stok.some((b) => Number(pakai[b.nama] || 0) > b.tersedia + 1e-6);
  const biayaKul = Math.round(stok.reduce((a, b) => a + Number(pakai[b.nama] || 0) * b.harga_rata, 0));
  const luar = rows.reduce((a, r) => a + Number(r.biaya || 0), 0);
  const upd = (k) => (e) => setForm({ ...form, [k]: e && e.target ? e.target.value : e });

  const save = async (e) => {
    e?.preventDefault();
    if (!form.tanggal_mulai) { toast.error("Tanggal cetak wajib diisi"); return; }
    if (lebih) { toast.error("Ada bahan yang melebihi stok"); return; }
    if (!(kg > 0) && !(luar > 0)) { toast.error("Isi bahan dari stok (kg) atau biaya bahan dari luar"); return; }
    const payload = { tanggal_mulai: form.tanggal_mulai, produk_id: form.produk_id || null,
      bahan_stok: stok.filter((b) => Number(pakai[b.nama] || 0) > 0).map((b) => ({ nama: b.nama, kg: Number(pakai[b.nama]) })),
      bahan_luar: rows.map((r) => ({ nama: r.nama || "", biaya: Number(r.biaya || 0) })),
      biaya_operasional: Number(form.biaya_operasional || 0), durasi_hari: Number(form.durasi_hari || 0), keterangan: form.keterangan || "" };
    try {
      if (item) await api.put(`/batch-briket/${item.id}`, payload);
      else await api.post("/batch-briket", payload);
      toast.success(item ? "Batch briket diperbarui" : "Briket dicetak — status: sedang dijemur/oven");
      setOpen(false); onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="briket-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Boxes size={18} className="text-violet-600"/>{item ? "Ubah Produksi Briket" : "Cetak Briket"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2 space-y-1.5"><Label>Produk Briket <span className="text-rose-500">*</span></Label>
            <Select value={form.produk_id || ""} onValueChange={upd("produk_id")}>
              <SelectTrigger data-testid="briket-produk"><SelectValue placeholder="Pilih produk"/></SelectTrigger>
              <SelectContent>{produkList.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Tanggal Cetak <span className="text-rose-500">*</span></Label><Input type="date" value={form.tanggal_mulai || ""} onChange={upd("tanggal_mulai")} data-testid="briket-tanggal"/></div>
          <div className="space-y-1.5"><Label>Durasi Jemur/Oven (hari)</Label><Input type="number" min="0" value={form.durasi_hari} onChange={upd("durasi_hari")} data-testid="briket-durasi"/></div>
          <div className="md:col-span-2 space-y-1.5 border border-violet-200 bg-violet-50/40 rounded-sm p-3"><Label>Bahan dari Stok (Kg) <span className="text-slate-400 font-normal">· kulitan, arang pendam, batok, dll</span></Label>
            <StokRows stok={stok} pakai={pakai} setPakai={setPakai}/>
            {lebih && <div className="text-[11px] text-rose-600" data-testid="briket-stok-error">Ada bahan yang melebihi stok tersedia</div>}
          </div>
          <div className="md:col-span-2 space-y-1.5"><Label>Bahan Tambahan Beli Luar (dicatat sebagai biaya)</Label><BahanRows rows={rows} setRows={setRows}/></div>
          <div className="md:col-span-2 space-y-1.5"><Label>Biaya Operasional Cetak (Rp)</Label><CurrencyInput value={form.biaya_operasional} onValueChange={upd("biaya_operasional")} data-testid="briket-op"/></div>
          <div className="md:col-span-2 grid grid-cols-3 gap-2 text-xs">
            <div className="border border-slate-200 bg-slate-50 p-2"><div className="text-slate-500">Bahan Stok ({kgFmt(kg)})</div><div className="font-mono font-semibold" data-testid="briket-biaya-stok">{rupiah(biayaKul)}</div></div>
            <div className="border border-slate-200 bg-slate-50 p-2"><div className="text-slate-500">Bahan Luar</div><div className="font-mono font-semibold" data-testid="briket-biaya-luar">{rupiah(luar)}</div></div>
            <div className="border border-slate-200 bg-blue-50 p-2"><div className="text-slate-500">Total s.d. Cetak</div><div className="font-mono font-semibold brand-text" data-testid="briket-total">{rupiah(biayaKul + luar + Number(form.biaya_operasional || 0))}</div></div>
          </div>
          <div className="md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan || ""} onChange={upd("keterangan")} data-testid="briket-keterangan"/></div>
          <button type="submit" className="hidden" aria-hidden="true"/>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button>
          <Button onClick={save} className="brand-btn rounded-sm" data-testid="briket-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
